import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';

export interface TimePickerProps {
  /** "HH:MM" 24-hour string, or '' / null when empty. Same contract as `<input type="time">`. */
  value: string | null | undefined;
  onChange: (next: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  /** Bounds of the slot list (24-hour). Defaults to a typical clinic window. */
  minHour?: number;
  maxHour?: number;
  /** Slot increment, in minutes. Defaults to 15. */
  stepMinutes?: number;
  className?: string;
  wrapperClassName?: string;
  id?: string;
  name?: string;
  onBlur?: () => void;
}

const HHMM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Convert "HH:MM" → 12-hour display (e.g., "1:20 PM"). Returns '' for invalid/empty. */
function format12h(hhmm: string | null | undefined): string {
  if (!hhmm) return '';
  const m = HHMM_RE.exec(hhmm);
  if (!m) return '';
  const h = Number(m[1]);
  const mm = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  const display = ((h + 11) % 12) + 1;
  return `${display}:${mm} ${period}`;
}

/**
 * Best-effort parse of free-text user input into "HH:MM" 24-hour, or null
 * when unparseable. Accepts:
 *   "13:23", "1:23 PM", "1:23pm", "1:23p", "1pm", "1 PM", "0930", "930", "23"
 */
function parseTimeInput(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;

  // Period suffix
  let period: 'am' | 'pm' | null = null;
  let body = s;
  if (body.endsWith('am') || body.endsWith('a')) {
    period = 'am';
    body = body.replace(/a(m)?$/, '');
  } else if (body.endsWith('pm') || body.endsWith('p')) {
    period = 'pm';
    body = body.replace(/p(m)?$/, '');
  }

  let hour = NaN;
  let minute = NaN;

  if (body.includes(':')) {
    const [h, m] = body.split(':');
    hour = Number(h);
    minute = Number(m);
  } else if (/^\d{3,4}$/.test(body)) {
    // "0930" or "930"
    const padded = body.padStart(4, '0');
    hour = Number(padded.slice(0, 2));
    minute = Number(padded.slice(2));
  } else if (/^\d{1,2}$/.test(body)) {
    hour = Number(body);
    minute = 0;
  } else {
    return null;
  }

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (minute < 0 || minute > 59) return null;
  if (hour < 0 || hour > 23) return null;

  if (period === 'pm' && hour < 12) hour += 12;
  else if (period === 'am' && hour === 12) hour = 0;

  return `${pad2(hour)}:${pad2(minute)}`;
}

/** Round a "HH:MM" to the nearest slot in the configured grid. */
function snapToStep(hhmm: string, stepMinutes: number): string {
  const m = HHMM_RE.exec(hhmm);
  if (!m) return hhmm;
  const total = Number(m[1]) * 60 + Number(m[2]);
  const snapped = Math.round(total / stepMinutes) * stepMinutes;
  const h = Math.min(23, Math.max(0, Math.floor(snapped / 60)));
  const mm = snapped % 60;
  return `${pad2(h)}:${pad2(mm)}`;
}

/** Add minutes to a "HH:MM" and return the bounded "HH:MM" result. */
function addMinutesHHMM(hhmm: string, minutes: number): string {
  const m = HHMM_RE.exec(hhmm);
  if (!m) return hhmm;
  const total = Number(m[1]) * 60 + Number(m[2]) + minutes;
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

/**
 * TimePicker — accessible 12-hour time picker with:
 *   - Styled trigger that mirrors the form Input look-and-feel
 *   - Popover with stepper buttons, free-text entry, and a scrollable
 *     15-min slot list (configurable)
 *   - Auto-scrolls the active slot into view on open
 *   - Keyboard: ArrowUp/Down to step the active slot, Enter to commit,
 *     Esc / click-outside to close
 *   - Drop-in compatible with the `value`/`onChange` contract of
 *     `<input type="time">` so it can replace it inside React-Hook-Form
 *     `Controller` blocks without other plumbing changes.
 */
export function TimePicker({
  value,
  onChange,
  label,
  hint,
  error,
  required,
  disabled,
  readOnly,
  placeholder = '--:--',
  minHour = 6,
  maxHour = 21,
  stepMinutes = 15,
  className,
  wrapperClassName,
  id,
  name,
  onBlur,
}: TimePickerProps) {
  const reactId = useId();
  const inputId = id ?? `tp-${reactId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;

  const normalized = value && HHMM_RE.test(value) ? value : '';
  const display = normalized ? format12h(normalized) : '';

  const [open, setOpen] = useState(false);
  /** Touch tier. Drives outside-dismissal and whether a search field autofocuses. */
  const isTouchTier = useIsBelow('md');
  const [draft, setDraft] = useState<string>(display);
  const [activeIdx, setActiveIdx] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const draftInputRef = useRef<HTMLInputElement>(null);

  const slots = useMemo(() => {
    const out: string[] = [];
    const startTotal = minHour * 60;
    const endTotal = maxHour * 60;
    for (let t = startTotal; t <= endTotal; t += stepMinutes) {
      const h = Math.floor(t / 60);
      const m = t % 60;
      out.push(`${pad2(h)}:${pad2(m)}`);
    }
    return out;
  }, [minHour, maxHour, stepMinutes]);

  const findClosestIdx = useCallback(
    (hhmm: string): number => {
      if (!hhmm) return 0;
      const target = Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
      let bestIdx = 0;
      let bestDelta = Infinity;
      for (let i = 0; i < slots.length; i += 1) {
        const s = slots[i];
        const tot = Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
        const delta = Math.abs(tot - target);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestIdx = i;
        }
      }
      return bestIdx;
    },
    [slots],
  );

  useEffect(() => {
    setDraft(display);
  }, [display]);

  const openPopover = useCallback(() => {
    if (disabled || readOnly) return;
    const idx = normalized ? findClosestIdx(normalized) : findClosestIdx(`${pad2(minHour)}:00`);
    setActiveIdx(idx);
    setDraft(display);
    setOpen(true);
  }, [disabled, readOnly, normalized, findClosestIdx, minHour, display]);

  const closePopover = useCallback(
    (commitDraft: boolean) => {
      if (commitDraft) {
        const parsed = parseTimeInput(draft);
        if (parsed && parsed !== normalized) {
          onChange(parsed);
        }
      }
      setOpen(false);
    },
    [draft, normalized, onChange],
  );

  // Click-outside
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        closePopover(true);
      }
    }
    // Below `md`, `pointerdown`: iOS Safari dispatches no mouse event at all for a tap
    // on a non-interactive element, which leaves this panel undismissable on a phone.
    // Desktop deliberately stays on `mousedown`. Same treatment, and same reasoning, as
    // `Select.tsx` — these are its siblings and carried the identical defect.
    const outsideEvent = isTouchTier ? 'pointerdown' : 'mousedown';
    document.addEventListener(outsideEvent, onDocMouseDown);
    return () => document.removeEventListener(outsideEvent, onDocMouseDown);
  }, [open, closePopover, isTouchTier]);

  // Auto-scroll active option into view when the popover opens or selection moves
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const node = list.querySelector<HTMLLIElement>(`[data-idx="${activeIdx}"]`);
    if (node) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIdx]);

  const commitSlot = (idx: number) => {
    const slot = slots[idx];
    if (!slot) return;
    onChange(slot);
    setOpen(false);
  };

  const stepBy = (minutes: number) => {
    const base = normalized || `${pad2(minHour)}:00`;
    const next = snapToStep(addMinutesHHMM(base, minutes), stepMinutes);
    onChange(next);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || readOnly) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPopover();
    }
  };

  const onPopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(slots.length - 1, (i < 0 ? 0 : i) + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, (i < 0 ? 0 : i) - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const parsed = parseTimeInput(draft);
      if (parsed) {
        onChange(parsed);
        setOpen(false);
      } else if (activeIdx >= 0) {
        commitSlot(activeIdx);
      }
    }
  };

  const triggerClass = cn(
    'form-input-base inline-flex w-full items-center justify-between gap-2 text-left',
    'pl-9 pr-2',
    error && 'form-input-error',
    (disabled || readOnly) && 'cursor-not-allowed bg-slate-50 text-slate-500',
    !disabled && !readOnly && 'cursor-pointer hover:bg-slate-50',
    className,
  );

  return (
    <div className={cn('w-full', wrapperClassName)} ref={wrapperRef}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <Clock className="h-3.5 w-3.5" />
        </div>

        <button
          id={inputId}
          type="button"
          name={name}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={!!error}
          aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
          disabled={disabled}
          className={triggerClass}
          onClick={openPopover}
          onKeyDown={onTriggerKeyDown}
          onBlur={onBlur}
        >
          <span className={cn('truncate text-sm', !display && 'text-slate-400')}>
            {display || placeholder}
          </span>
          <span className="flex shrink-0 items-center text-slate-400">
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </button>

        {open && (
          <div
            role="dialog"
            aria-label={label ? `${label} picker` : 'Time picker'}
            onKeyDown={onPopoverKeyDown}
            className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg focus:outline-none"
          >
            <div className="flex items-center gap-1 border-b border-slate-100 p-2">
              <input
                ref={draftInputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const parsed = parseTimeInput(draft);
                    if (parsed) {
                      onChange(parsed);
                      setOpen(false);
                    }
                  }
                }}
                placeholder="e.g. 1:20 PM"
                className="form-input-base h-8 flex-1 px-2 text-sm"
                aria-label="Type a time"
                // Same reasoning as the other dropdowns: on a touch screen this raised
                // a keyboard over the hour/minute columns the popover exists to offer.
                autoFocus={!isTouchTier}
              />
              <button
                type="button"
                aria-label={`Step back ${stepMinutes} minutes`}
                title={`-${stepMinutes} min`}
                onClick={() => stepBy(-stepMinutes)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <ChevronUp className="h-3.5 w-3.5 rotate-180" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={`Step forward ${stepMinutes} minutes`}
                title={`+${stepMinutes} min`}
                onClick={() => stepBy(stepMinutes)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>

            <ul
              ref={listRef}
              role="listbox"
              aria-label="Time options"
              className="max-h-56 overflow-y-auto py-1"
            >
              {slots.map((slot, idx) => {
                const isCurrent = slot === normalized;
                const isActive = idx === activeIdx;
                return (
                  <li
                    key={slot}
                    data-idx={idx}
                    role="option"
                    aria-selected={isCurrent}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => commitSlot(idx)}
                    className={cn(
                      'cursor-pointer select-none px-3 py-1.5 text-sm',
                      isActive ? 'bg-teal-50 text-teal-900' : 'text-slate-700',
                      isCurrent && !isActive && 'font-semibold text-slate-900',
                    )}
                  >
                    {format12h(slot)}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

TimePicker.displayName = 'TimePicker';
